import React, { useState, useEffect, ReactNode } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Ban, Archive, AlertCircle, Zap, Loader, ShieldAlert, CheckCircle2, Mail, Users, ArrowRight, ShieldCheck, MailX } from 'lucide-react';
import { format } from 'date-fns';
import EmailActionModal from '../components/EmailActionModal';
import MainLayout from '../components/layout/MainLayout';

import { dashboardApi, IncomingEmailAssessment, IncomingEmailStatsResponse } from '../services/dashboardApi';

interface RowProps {
    row: any;
    isSelected: boolean;
    onToggleSelect: (messageId: string) => void;
    onAction: (action: string, email: any) => void;
}

const Row = ({ row, isSelected, onToggleSelect, onAction }: RowProps) => {
    const [open, setOpen] = useState(false);

    const getIntentColor = (intent: string) => {
        switch (intent) {
            case 'UNSUBSCRIBE': return 'bg-red-100 text-red-800';
            case 'COMPLAINT': return 'bg-rose-100 text-rose-800';
            case 'PRAISE': return 'bg-green-100 text-green-800';
            case 'INQUIRY': return 'bg-blue-100 text-blue-800';
            case 'NOTIFICATION': return 'bg-amber-100 text-amber-800';
            case 'BOUNCE': return 'bg-orange-100 text-orange-800';
            case 'BOUNCE_FAIL': return 'bg-orange-100 text-orange-800';
            case 'OOO': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <>
            <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-100">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(row.message_id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                </td>
                <td className="px-4 py-3">
                    <button
                        onClick={() => setOpen(!open)}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                    {row.received_at ? format(new Date(row.received_at), 'MMM dd, HH:mm') : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 font-medium">{row.sender}</td>
                <td className="px-4 py-3 text-sm text-gray-500 max-w-[150px] truncate" title={row.receiver}>{row.receiver || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate" title={row.subject}>{row.subject}</td>
                <td className="px-4 py-3">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getIntentColor(row.intent)}`}>
                        {row.intent}
                    </span>
                </td>
                <td className="px-4 py-3">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                        {row.sentiment}
                    </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                    {row.status === 'PROCESSED_AUTO' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                            ✓ Auto-Handled
                        </span>
                    ) : (
                        <>
                            <button
                                onClick={() => onAction('blacklist', row)}
                                className="text-red-600 hover:text-red-900 flex items-center gap-1 mb-2"
                            >
                                <Ban size={14} /> Block & Unsub
                            </button>
                            <button
                                onClick={() => onAction('ignore', row)}
                                className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                            >
                                <Archive size={14} /> Ignore/Archive
                            </button>
                        </>
                    )}
                </td>
            </tr>
            {open && (
                <tr className="bg-gray-50">
                    <td colSpan={9} className="px-4 py-4">
                        <div className="pl-12">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Analysis & Content</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <span className="text-xs text-gray-500 uppercase">Summary</span>
                                    <p className="text-sm text-gray-700">{row.summary}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-gray-500 uppercase">Suggested Action</span>
                                    <p className="text-sm text-gray-700 font-medium">{row.suggested_action}</p>
                                </div>
                            </div>
                            <div className="bg-white p-3 rounded border border-gray-200">
                                <span className="text-xs text-gray-500 uppercase mb-2 block">Message Content</span>
                                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans max-h-96 overflow-y-auto">
                                    {row.email_body || row.body_snippet || "No body content available."}
                                </pre>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default function IncomingEmails() {
    const [emails, setEmails] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<any>(null);
    const [actionType, setActionType] = useState<string>('');
    const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const PAGE_SIZE = 50;

    // Filters & Metrics
    const [statusTab, setStatusTab] = useState<'PENDING' | 'PROCESSED_AUTO' | 'ALL'>('PENDING');
    const [intentFilter, setIntentFilter] = useState<string>('ALL');
    const [stats, setStats] = useState<IncomingEmailStatsResponse | null>(null);
    const [isTriagingBacklog, setIsTriagingBacklog] = useState(false);

    // --- Queue States ---
    const [manualQueueInput, setManualQueueInput] = useState('');
    const [unsubscribeQueue, setUnsubscribeQueue] = useState<{ email: string; source: string; status: 'Pending' | 'Processing' | 'Success' | 'Failed', messageId?: string }[]>([]);
    const [ignoreQueue, setIgnoreQueue] = useState<{ email: string; subject: string; source: string; status: 'Pending' | 'Processing' | 'Success' | 'Failed', messageId: string }[]>([]);

    const handleBulkAction = async (action: string) => {
        if (selectedMessageIds.size === 0) return;
        if (!window.confirm(`Are you sure you want to ${action} ${selectedMessageIds.size} emails?`)) return;

        const messageIdsArray = Array.from(selectedMessageIds);
        const selectedEmailsData = emails.filter(e => selectedMessageIds.has(e.message_id));

        if (action === 'blacklist') {
            const newQueueItems = selectedEmailsData.map(e => ({
                email: e.sender,
                source: e.receiver || 'Bulk Selection',
                status: 'Pending' as const,
                messageId: e.message_id
            }));
            setUnsubscribeQueue(prev => [...newQueueItems, ...prev]);
        } else if (action === 'ignore') {
            const newIgnoreItems = selectedEmailsData.map(e => ({
                email: e.sender,
                subject: e.subject,
                source: e.receiver || 'Bulk Selection',
                status: 'Pending' as const,
                messageId: e.message_id
            }));
            setIgnoreQueue(prev => [...newIgnoreItems, ...prev]);
        } else {
            // fallback (although 'ignore' is the only other explicit one right now)
            try {
                await dashboardApi.processIncomingEmailAction(messageIdsArray, action);
            } catch (error) {
                console.error(error);
                alert(`Failed to bulk ${action} emails.`);
            }
        }

        // Remove from list
        setEmails(emails.filter(e => !selectedMessageIds.has(e.message_id)));
        setSelectedMessageIds(new Set());
    };

    const handleAutoProcessAll = async () => {
        const processableEmails = emails.filter(e => e.intent !== 'INQUIRY');

        if (processableEmails.length === 0) {
            alert("No emails applicable for Auto-Processing (Inquiries are skipped).");
            return;
        }

        if (!window.confirm(`Auto-process ${processableEmails.length} emails? (Stages Unsubscribes/Bounces into Queue, Archives the rest while leaving Inquiries alone)`)) return;

        try {
            const processBlacklist = processableEmails.filter(e => e.intent === 'UNSUBSCRIBE' || e.intent === 'COMPLAINT' || e.intent === 'BOUNCE_FAIL');
            const processIgnore = processableEmails.filter(e => !(e.intent === 'UNSUBSCRIBE' || e.intent === 'COMPLAINT' || e.intent === 'BOUNCE_FAIL'));

            if (processBlacklist.length > 0) {
                setUnsubscribeQueue(prev => [
                    ...processBlacklist.map(e => ({ email: e.sender, source: 'Auto-Process', status: 'Pending' as const, messageId: e.message_id })),
                    ...prev
                ]);
            }

            if (processIgnore.length > 0) {
                setIgnoreQueue(prev => [
                    ...processIgnore.map(e => ({ email: e.sender, subject: e.subject, source: 'Auto-Process', status: 'Pending' as const, messageId: e.message_id })),
                    ...prev
                ]);
            }

            // Remove processed emails from view
            const processedIds = new Set(processableEmails.map(e => e.message_id));
            setEmails(emails.filter(e => !processedIds.has(e.message_id)));

            // Un-select if they were previously checked
            setSelectedMessageIds(prev => {
                const newSet = new Set(prev);
                processedIds.forEach(id => newSet.delete(id));
                return newSet;
            });

        } catch (error) {
            console.error(error);
            alert("Failed to Auto-Process emails.");
        }
    };

    const toggleSelectAll = () => {
        if (selectedMessageIds.size === emails.length) {
            setSelectedMessageIds(new Set());
        } else {
            setSelectedMessageIds(new Set(emails.map(e => e.message_id)));
        }
    };

    const handleToggleSelectRow = (messageId: string) => {
        setSelectedMessageIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(messageId)) {
                newSet.delete(messageId);
            } else {
                newSet.add(messageId);
            }
            return newSet;
        });
    };

    const handleProcessQueue = async () => {
        const pendingItems = unsubscribeQueue.filter(q => q.status === 'Pending');
        if (pendingItems.length === 0) return;

        setUnsubscribeQueue(prev => prev.map(q => q.status === 'Pending' ? { ...q, status: 'Processing' } : q));

        const tableItems = pendingItems.filter(q => q.messageId);
        const pasteItems = pendingItems.filter(q => !q.messageId);

        const successEmails = new Set<string>();
        const failedEmails = new Set<string>();

        // 1. Process table Items
        if (tableItems.length > 0) {
            const messageIdsArray = tableItems.map(q => q.messageId!);
            try {
                await dashboardApi.processIncomingEmailAction(messageIdsArray, 'blacklist');
                tableItems.forEach(q => successEmails.add(q.email));
            } catch (error) {
                console.error("Failed table items unsubscribe", error);
                tableItems.forEach(q => failedEmails.add(q.email));
            }
        }

        // 2. Process paste items
        if (pasteItems.length > 0) {
            const emailsArray = pasteItems.map(q => q.email);
            try {
                await dashboardApi.processManualUnsubscribeQueue(emailsArray);
                pasteItems.forEach(q => successEmails.add(q.email));
            } catch (error) {
                console.error("Failed manual items unsubscribe", error);
                pasteItems.forEach(q => failedEmails.add(q.email));
            }
        }

        setUnsubscribeQueue(prev => prev.map(q => {
            if (q.status !== 'Processing') return q;
            if (successEmails.has(q.email)) return { ...q, status: 'Success' };
            if (failedEmails.has(q.email)) return { ...q, status: 'Failed' };
            return q;
        }));
    };

    const handleProcessIgnoreQueue = async () => {
        const pendingItems = ignoreQueue.filter(q => q.status === 'Pending');
        if (pendingItems.length === 0) return;

        setIgnoreQueue(prev => prev.map(q => q.status === 'Pending' ? { ...q, status: 'Processing' } : q));

        const successEmails = new Set<string>();
        const failedEmails = new Set<string>();

        const messageIdsArray = pendingItems.map(q => q.messageId);
        try {
            await dashboardApi.processIncomingEmailAction(messageIdsArray, 'ignore');
            pendingItems.forEach(q => successEmails.add(q.email));
        } catch (error) {
            console.error("Failed table items ignore", error);
            pendingItems.forEach(q => failedEmails.add(q.email));
        }

        setIgnoreQueue(prev => prev.map(q => {
            if (q.status !== 'Processing') return q;
            if (successEmails.has(q.email)) return { ...q, status: 'Success' };
            if (failedEmails.has(q.email)) return { ...q, status: 'Failed' };
            return q;
        }));
    };

    const loadEmails = async (append = false, tab = statusTab, intent = intentFilter) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setSelectedMessageIds(new Set());
        }
        try {
            const currentOffset = append ? offset : 0;
            const data = await dashboardApi.getIncomingEmails(PAGE_SIZE, currentOffset, tab, intent);
            if (append) {
                setEmails(prev => [...prev, ...data]);
            } else {
                setEmails(data);
            }
            setOffset(currentOffset + data.length);
        } catch (e) {
            console.error("Failed to load emails", e);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadCount = async (tab = statusTab, intent = intentFilter) => {
        try {
            const { total } = await dashboardApi.getIncomingEmailsCount(tab, intent);
            setTotalCount(total);
        } catch (e) {
            console.error("Failed to load email count", e);
        }
    };

    const loadStats = async () => {
        try {
            const res = await dashboardApi.getIncomingEmailStats();
            if (res && res.success) {
                setStats(res);
            }
        } catch (e) {
            console.error("Failed to load email stats", e);
        }
    };

    const handleRunAutoTriage = async () => {
        if (!window.confirm("Auto-triage pending backlog? This will automatically unsubscribe pending unsubscribes/complaints, clear dead mailboxes in database, and archive auto-responders.")) {
            return;
        }
        setIsTriagingBacklog(true);
        try {
            const res = await dashboardApi.autoTriagePendingBacklog(200);
            if (res && res.success) {
                alert(`Auto-triage completed!\n• Processed: ${res.processed} emails\n• Unsubscribed: ${res.stats?.unsubscribed || 0}\n• Complaints: ${res.stats?.complaints || 0}\n• Bounced/Dead mailboxes: ${res.stats?.bounced || 0}\n• Archived: ${res.stats?.archived || 0}`);
                loadEmails(false, statusTab, intentFilter);
                loadCount(statusTab, intentFilter);
                loadStats();
            } else {
                alert(`Notice: ${res?.error || 'No pending automated emails to process.'}`);
            }
        } catch (e) {
            console.error(e);
            alert("Auto-triage request failed.");
        } finally {
            setIsTriagingBacklog(false);
        }
    };

    useEffect(() => {
        loadEmails(false, statusTab, intentFilter);
        loadCount(statusTab, intentFilter);
        loadStats();
    }, [statusTab, intentFilter]);

    const handleActionClick = (action: string, email: any) => {
        if (action === 'blacklist') {
            // Push to queue
            setUnsubscribeQueue(prev => [{
                email: email.sender,
                source: email.receiver || 'Incoming Emails',
                status: 'Pending',
                messageId: email.message_id
            }, ...prev]);

            // Remove from main list visually
            setEmails(prev => prev.filter(e => e.message_id !== email.message_id));
            setSelectedMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(email.message_id);
                return newSet;
            });
        } else if (action === 'ignore') {
            // Push to ignore queue
            setIgnoreQueue(prev => [{
                email: email.sender,
                subject: email.subject,
                source: email.receiver || 'Incoming Emails',
                status: 'Pending',
                messageId: email.message_id
            }, ...prev]);

            // Remove from main list visually
            setEmails(prev => prev.filter(e => e.message_id !== email.message_id));
            setSelectedMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(email.message_id);
                return newSet;
            });
        } else {
            setSelectedEmail(email);
            setActionType(action);
            setModalOpen(true);
        }
    };

    const handleConfirmAction = async (notes: string) => {
        try {
            await dashboardApi.processIncomingEmailAction(selectedEmail.message_id, actionType);

            // Remove from list
            setEmails(emails.filter(e => e.message_id !== selectedEmail.message_id));
            setSelectedMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedEmail.message_id);
                return newSet;
            });

            setModalOpen(false);
        } catch (error) {
            console.error(error);
            alert("Failed to process action.");
        }
    };

    return (
        <MainLayout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Incoming Emails
                        {totalCount > 0 && (
                            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                {totalCount} unhandled
                            </span>
                        )}
                    </h1>
                    <div className="flex items-center gap-3">
                        {selectedMessageIds.size > 0 && (
                            <>
                                <span className="text-sm text-gray-600">{selectedMessageIds.size} selected</span>
                                <button
                                    onClick={() => handleBulkAction('blacklist')}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded text-sm font-medium text-red-700 hover:bg-red-100"
                                >
                                    <Ban size={14} /> Bulk Block & Unsub
                                </button>
                                <button
                                    onClick={() => handleBulkAction('ignore')}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-200"
                                >
                                    <Archive size={14} /> Bulk Ignore
                                </button>
                            </>
                        )}
                        {unsubscribeQueue.filter(q => q.status === 'Pending').length > 0 && (
                            <button
                                onClick={handleProcessQueue}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 shadow-sm transition-all"
                            >
                                <Ban size={16} /> Process Bulk Unsubscribe ({unsubscribeQueue.filter(q => q.status === 'Pending').length})
                            </button>
                        )}
                        <button
                            onClick={handleAutoProcessAll}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-md text-sm font-medium text-indigo-700 hover:bg-indigo-100 shadow-sm"
                            title="Automatically Stages Unsubscribes/Bounces in Queue & Archives The Rest (skips Inquiries)"
                        >
                            <Zap size={16} className="text-indigo-500" /> Auto-Process All
                        </button>
                        <button
                            onClick={() => { loadEmails(false, statusTab, intentFilter); loadCount(statusTab, intentFilter); loadStats(); }}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            <RefreshCw size={16} /> Refresh
                        </button>
                    </div>
                </div>

                {/* --- KPI Summary Cards --- */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pending Review</span>
                            <span className="p-2 bg-blue-50 text-blue-600 rounded-md"><Mail size={18} /></span>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-gray-900">{stats?.kpis?.pending ?? (statusTab === 'PENDING' ? totalCount : '-')}</span>
                            <span className="text-xs text-gray-500">needs triage</span>
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Auto-Unsubscribed</span>
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-md"><CheckCircle2 size={18} /></span>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-emerald-600">{stats?.kpis?.unsubscribes ?? '-'}</span>
                            <span className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">Global ESP synced</span>
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Complaints Neutralized</span>
                            <span className="p-2 bg-rose-50 text-rose-600 rounded-md"><ShieldAlert size={18} /></span>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-rose-600">{stats?.kpis?.complaints ?? '-'}</span>
                            <span className="text-xs text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded font-medium">Blacklisted</span>
                        </div>
                    </div>

                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bounced / Dead Mailboxes</span>
                            <span className="p-2 bg-amber-50 text-amber-600 rounded-md"><MailX size={18} /></span>
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-amber-600">{stats?.kpis?.bounces ?? '-'}</span>
                            <span className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded font-medium">Deactivated</span>
                        </div>
                    </div>
                </div>

                {/* --- Controls & Filter Bar --- */}
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6 flex flex-wrap items-center justify-between gap-4">
                    {/* Status Tabs */}
                    <div className="flex items-center bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setStatusTab('PENDING')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${statusTab === 'PENDING' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            Needs Review {stats?.kpis?.pending !== undefined && `(${stats.kpis.pending})`}
                        </button>
                        <button
                            onClick={() => setStatusTab('PROCESSED_AUTO')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${statusTab === 'PROCESSED_AUTO' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            Auto-Handled {stats?.kpis?.processed_auto !== undefined && `(${stats.kpis.processed_auto})`}
                        </button>
                        <button
                            onClick={() => setStatusTab('ALL')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${statusTab === 'ALL' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            All Emails {stats?.kpis?.total !== undefined && `(${stats.kpis.total})`}
                        </button>
                    </div>

                    {/* Intent Filter & Actions */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span>Intent:</span>
                            <select
                                value={intentFilter}
                                onChange={(e) => setIntentFilter(e.target.value)}
                                className="text-xs border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-1.5 pl-2 pr-6"
                            >
                                <option value="ALL">All Intents</option>
                                <option value="UNSUBSCRIBE">Unsubscribe</option>
                                <option value="COMPLAINT">Complaint</option>
                                <option value="BOUNCE">Bounce / Mailbox Full</option>
                                <option value="BOUNCE_FAIL">Delivery Failure</option>
                                <option value="INQUIRY">Inquiry</option>
                                <option value="PRAISE">Praise</option>
                                <option value="OOO">Out of Office</option>
                                <option value="NOTIFICATION">Notifications</option>
                                <option value="OTHER">Other</option>
                            </select>
                        </div>

                        <button
                            onClick={handleRunAutoTriage}
                            disabled={isTriagingBacklog}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md text-xs font-medium shadow-sm hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50"
                            title="Auto-triages unhandled unsubscribes, complaints, and bounces in the database backlog"
                        >
                            {isTriagingBacklog ? (
                                <Loader size={14} className="animate-spin" />
                            ) : (
                                <Zap size={14} />
                            )}
                            Auto-Triage Backlog
                        </button>
                    </div>
                </div>

                {/* Top Receiving Domains Pill Bar */}
                {stats?.domains && stats.domains.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-6 text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-700">Top Receiving Mailboxes:</span>
                        {stats.domains.map(d => (
                            <span key={d.receiver} className="bg-white px-2.5 py-1 rounded border border-gray-200 font-mono text-[11px] text-gray-800">
                                {d.receiver} <strong className="text-indigo-600">({d.count})</strong>
                            </span>
                        ))}
                    </div>
                )}

                {/* --- Unsubscribe Queue Section --- */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200 mb-8">
                    <div className="px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
                                <Ban size={18} className="text-red-500" /> Unsubscribe Queue
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                Emails pasted below or "Blocked" below will be explicitly unsubscribed globally.
                            </p>
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <label htmlFor="manualQueue" className="block text-sm font-medium text-gray-700 mb-2">
                            Add Emails to Queue (comma or newline separated)
                        </label>
                        <div className="flex gap-4 items-start">
                            <textarea
                                id="manualQueue"
                                rows={3}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="test1@example.com, test2@example.com&#10;test3@example.com"
                                value={manualQueueInput}
                                onChange={(e) => setManualQueueInput(e.target.value)}
                            />
                            <button
                                onClick={() => {
                                    if (!manualQueueInput.trim()) return;
                                    // split by commas or newlines
                                    const rawEmails = manualQueueInput.split(/[\n,]+/);
                                    const validEmails = rawEmails.map(e => e.trim()).filter(e => e.includes('@'));

                                    if (validEmails.length > 0) {
                                        const newEntries = validEmails.map(e => ({
                                            email: e,
                                            source: 'Manual Paste',
                                            status: 'Pending' as const
                                        }));
                                        setUnsubscribeQueue(prev => [...newEntries, ...prev]);
                                        setManualQueueInput('');
                                    }
                                }}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                            >
                                Add to Queue
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-white">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Address</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {unsubscribeQueue.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-6 text-center text-sm text-gray-500">
                                            The queue is empty.
                                        </td>
                                    </tr>
                                ) : (
                                    unsubscribeQueue.map((item, idx) => (
                                        <tr key={idx} className={item.status === 'Success' ? 'bg-green-50' : item.status === 'Failed' ? 'bg-red-50' : ''}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.source}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {item.status === 'Pending' && <span className="text-gray-500 font-medium">Pending...</span>}
                                                {item.status === 'Processing' && <span className="text-blue-600 font-medium flex items-center gap-1"><RefreshCw size={14} className="animate-spin" /> Processing...</span>}
                                                {item.status === 'Success' && <span className="text-green-600 font-medium">Success</span>}
                                                {item.status === 'Failed' && <span className="text-red-600 font-medium">Failed</span>}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* --- Ignore / Archive Queue Section (Always Hidden if Empty) --- */}
            {ignoreQueue.length > 0 && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200 mb-8">
                    <div className="px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
                                <Archive size={18} className="text-gray-500" /> Ignore / Archive Queue
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                Emails selected to be "Ignored" or "Archived" will be processed and hidden from this view.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {ignoreQueue.filter(q => q.status === 'Pending').length > 0 && (
                                <button
                                    onClick={handleProcessIgnoreQueue}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-gray-700 shadow-sm transition-all"
                                >
                                    <Archive size={16} /> Process Bulk Ignore ({ignoreQueue.filter(q => q.status === 'Pending').length})
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-white">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Address</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {ignoreQueue.map((item, idx) => (
                                    <tr key={idx} className={item.status === 'Success' ? 'bg-green-50' : item.status === 'Failed' ? 'bg-red-50' : ''}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.email}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{item.subject}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.source}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {item.status === 'Pending' && <span className="text-gray-500 font-medium">Pending...</span>}
                                            {item.status === 'Processing' && <span className="text-blue-600 font-medium flex items-center gap-1"><RefreshCw size={14} className="animate-spin" /> Processing...</span>}
                                            {item.status === 'Success' && <span className="text-green-600 font-medium">Success</span>}
                                            {item.status === 'Failed' && <span className="text-red-600 font-medium">Failed</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="w-10 px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={emails.length > 0 && selectedMessageIds.size === emails.length}
                                        onChange={toggleSelectAll}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receiver</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Intent</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sentiment</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                                        Loading emails...
                                    </td>
                                </tr>
                            ) : emails.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                                        No emails found.
                                    </td>
                                </tr>
                            ) : (
                                emails.map((email) => (
                                    <Row
                                        key={email.message_id}
                                        row={email}
                                        isSelected={selectedMessageIds.has(email.message_id)}
                                        onToggleSelect={handleToggleSelectRow}
                                        onAction={handleActionClick}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Load More */}
                {!loading && emails.length > 0 && emails.length < totalCount && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                            Showing {emails.length} of {totalCount} unhandled emails
                        </span>
                        <button
                            onClick={() => loadEmails(true)}
                            disabled={loadingMore}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            {loadingMore ? (
                                <><Loader size={16} className="animate-spin" /> Loading...</>
                            ) : (
                                <>Load More ({Math.min(PAGE_SIZE, totalCount - emails.length)} more)</>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {selectedEmail && (
                <EmailActionModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    email={selectedEmail}
                    actionType={actionType}
                    onConfirm={handleConfirmAction}
                />
            )}
        </MainLayout>
    );
}
